"use client";

import React from "react";

import { FieldValues } from "react-hook-form";
import { z } from "zod";

import { FormError } from "./FormError";
import { UnifiedFormTemplateProps } from "./formInterfaces";
import { useUnifiedFormTemplate } from "./useFormSubmission";

/**
 * BaseFormTemplate handles all shared form logic and rendering.
 * It accepts a renderContainer prop to allow custom UI (modal, card, etc).
 */
export function BaseFormTemplate<
  TFormValues extends FieldValues,
  TSchema extends z.ZodType<TFormValues>,
>({
  renderContainer,
  renderForm,
  ...props
}: UnifiedFormTemplateProps<TFormValues> & {
  renderContainer: (containerProps: {
    control: any;
    error: string | null;
    form: React.ReactNode;
    formState: any;
    isSubmitting: boolean;
    reset: any;
    setValue: any;
    watch: any;
  }) => React.ReactNode;
  renderForm?: (formProps: {
    onSubmit: React.FormEventHandler<HTMLFormElement>;
    onKeyDown?: React.KeyboardEventHandler<HTMLFormElement>;
    children: React.ReactNode;
  }) => React.ReactNode;
}) {
  const {
    children,
    defaultValues,
    eventData = {},
    eventKeys,
    formSchema,
    onError,
    onSubmit,
    onSuccess,
  } = props;

  const {
    control,
    error,
    formState,
    handleSubmit,
    hookFormSubmit,
    isSubmitting,
    reset,
    setValue,
    watch,
  } = useUnifiedFormTemplate<TFormValues, TSchema>({
    defaultValues,
    eventData,
    eventKeys,
    formSchema,
    onError,
    onSubmit,
    onSuccess,
  });

  const formContent = (
    <>
      <FormError message={error} />
      {children({ control, formState, reset, setValue, watch })}
    </>
  );

  const form = renderForm ? (
    renderForm({
      children: formContent,
      onSubmit: (e) => {
        void hookFormSubmit((data) => handleSubmit(data))(e);
      },
    })
  ) : (
    <form
      noValidate
      onSubmit={(e) => {
        void hookFormSubmit((data) => {
          return handleSubmit(data);
        })(e);
      }}
    >
      {formContent}
    </form>
  );

  return renderContainer({
    control,
    error,
    form,
    formState,
    isSubmitting,
    reset,
    setValue,
    watch,
  });
}
